import { createContext, useContext, useState } from "react";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { IconButton, TableCell } from "@mui/material";
import TextInput from "src/Components/TextInput";
import { IDeltaOptionChainReport, IDeltaPortfolioReport, IDeltaSummaryReport } from "src/Interfaces/automaticMarketMakingDelta";
import { Header, ITableReport } from "src/Interfaces/TableInterfaces";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { IAdditional, ITableUI } from "src/Tables/TableTemplate/tableTemplate";

export const numberText = (value: number | null | undefined, digits = 4) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const text = value.toLocaleString("en-US", { maximumFractionDigits: digits });
  return text === "-0" ? "0" : text;
};

interface ISimulationActions {
  disabled: boolean;
  onAdd: (instrumentId: string, volume: number) => Promise<boolean>;
  onEdit: (row: IDeltaPortfolioReport, volume: number) => Promise<boolean>;
  onDelete: (id: number) => Promise<boolean>;
}

export const SimulationActionsContext = createContext<ISimulationActions>({
  disabled: false, onAdd: async () => false, onEdit: async () => false, onDelete: async () => false,
});

const PortfolioActions = ({ row }: { row: IDeltaPortfolioReport }) => {
  const { disabled, onEdit, onDelete } = useContext(SimulationActionsContext);
  const [editing, setEditing] = useState(false);
  const [volume, setVolume] = useState("");
  const parsed = Number(volume);
  const validVolume = volume.trim() !== "" && Number.isFinite(parsed) && parsed !== 0;
  return <TableCell>{row.simulationId !== null && (editing ? <div className="flex items-center gap-1 w-[calc(11rem*2/3)]">
    <TextInput name="volume" label="حجم" direction="ltr" kind="negativeDecimalNumbers" value={volume} onChange={(_, value) => setVolume(value ?? "")} disabled={disabled} />
    <IconButton size="small" aria-label={`ذخیره حجم ${row.symbol}`} disabled={!validVolume || disabled} onClick={async () => { if (await onEdit(row, parsed)) setEditing(false); }}><CheckOutlined /></IconButton>
    <IconButton size="small" aria-label="انصراف از ویرایش" disabled={disabled} onClick={() => setEditing(false)}><CloseOutlined /></IconButton>
  </div> : <>
    <IconButton size="small" aria-label={`ویرایش ${row.symbol}`} disabled={disabled} onClick={() => { setVolume(row.volume.toString()); setEditing(true); }}><EditOutlined /></IconButton>
    <IconButton size="small" aria-label={`حذف ${row.symbol}`} disabled={disabled} onClick={() => onDelete(row.simulationId!)}><DeleteOutlined /></IconButton>
  </>)}</TableCell>;
};

const ChainActions = ({ row }: { row: IDeltaOptionChainReport }) => {
  const { disabled, onAdd } = useContext(SimulationActionsContext);
  const [volume, setVolume] = useState("");
  const parsed = Number(volume);
  const valid = volume.trim() !== "" && Number.isFinite(parsed) && parsed !== 0;
  return <TableCell><div className="flex items-center gap-1 w-[calc(10rem*2/3)]">
    <TextInput name={row.instrumentId} label="تعداد" direction="ltr" kind="negativeDecimalNumbers" value={volume}
      onChange={(_, value) => setVolume(value ?? "")} disabled={disabled} />
    <IconButton size="small" aria-label={`افزودن ${row.symbol} به شبیه‌سازی`} disabled={!valid || disabled} onClick={async () => {
      if (await onAdd(row.instrumentId, parsed)) setVolume("");
    }}><PlusOutlined /></IconButton>
  </div></TableCell>;
};

const PortfolioAdditional: IAdditional<IDeltaPortfolioReport> = {
  getHeader: () => <TableCell><span className="!font-black text-black">عملیات</span></TableCell>, getRow: PortfolioActions,
};
const ChainAdditional: IAdditional<IDeltaOptionChainReport> = {
  getHeader: () => <TableCell className="!whitespace-pre-line !leading-5"><span className="!font-black text-black">{"اضافه به\nشبیه‌سازی"}</span></TableCell>, getRow: ChainActions,
};

// Format only the displayed value; calculations and API data retain their full precision.
const renderNumber = (row: ITableReport, header: Header) => {
  const key = header.headerName.charAt(0).toLowerCase() + header.headerName.slice(1);
  const value = (row as unknown as Record<string, unknown>)[key];
  if (typeof value !== "number" && value !== null) return undefined;
  const absolute = value === null ? 0 : Math.abs(value as number);
  const portfolioGamma = key === "positionGamma" || (key === "gamma" && "portfolio" in row);
  const digits = key === "delta" || key === "positionDelta" || key === "originalNeutralizingVolume" || key === "simulatedNeutralizingVolume"
    ? (absolute >= 100 ? 0 : 2)
    : portfolioGamma ? (absolute > 100 ? 0 : 5)
    : key === "gamma" ? 5 : key === "ivPercent" ? 1 : 4;
  return <span dir="ltr">{numberText(value as number | null, digits)}</span>;
};

const renderChainNumber = (row: IDeltaOptionChainReport, header: Header) => {
  if ((header.headerName === "OriginalNeutralizingVolume" || header.headerName === "SimulatedNeutralizingVolume") &&
    row.delta !== null && Math.abs(row.delta) < 0.001) return null;
  return renderNumber(row, header);
};

export const PortfolioTableUI = (props: ITableUI<IDeltaPortfolioReport>) => <CommonTableUI {...props} AdditionalBody={PortfolioAdditional}
  tableBoxClassName={`delta-calculations-table max-h-[calc(100dvh-280px)] overflow-y-auto ${props.tableBoxClassName ?? ""}`}
  tableHeaderClassName={`${props.tableHeaderClassName ?? ""} !whitespace-pre-line !leading-5`}
  getRowClassName={(row, index) => row.simulationId === null ? "" :
    `!bg-purple-50 [&_td]:!bg-purple-50 hover:[&_td]:!bg-purple-100 ${
      index === 0 || props.reports.report[index - 1].simulationId === null
        ? "[&>td]:!border-t-[3px] [&>td]:!border-t-purple-300" : ""}`}
  renderCell={renderNumber} />;

export const OptionChainTableUI = (props: ITableUI<IDeltaOptionChainReport>) => <CommonTableUI {...props} AdditionalBody={ChainAdditional}
  tableBoxClassName={`delta-calculations-table max-h-[calc(100dvh-280px)] overflow-y-auto ${props.tableBoxClassName ?? ""}`}
  tableHeaderClassName={`${props.tableHeaderClassName ?? ""} !whitespace-pre-line !leading-5`} renderCell={renderChainNumber} />;

export const SummaryTableUI = (props: ITableUI<IDeltaSummaryReport>) => <CommonTableUI {...props} renderCell={renderNumber} />;
